import type { SupabaseClient } from '@supabase/supabase-js';
import { validateUsername } from './username';
import { isSyntheticAddress } from '../config/ages';
import {
  ALREADY_REGISTERED,
  friendlySignupError,
  validateEmail,
  validatePassword,
} from './signupError';
import type { SavePayload } from './saveSync';
import { parseRole, type Role } from './roles';

export async function checkUsername(
  client: SupabaseClient,
  name: string,
): Promise<'ok' | 'taken' | 'invalid'> {
  if (validateUsername(name) !== null) return 'invalid';
  const { data, error } = await client.rpc('username_available', { name });
  if (error) return 'invalid';
  return data === true ? 'ok' : 'taken';
}

export async function register(
  client: SupabaseClient,
  email: string,
  password: string,
  username: string,
  redirectTo?: string,
  /**
   * The device's guest save, transferred to the account AT CREATION (before
   * email verification) by riding user metadata into the game_saves signup
   * trigger — the only channel that works while no session exists yet.
   */
  save?: SavePayload,
): Promise<{ ok: true; needsConfirm: boolean } | { ok: false; error: string }> {
  // Answer the three field mistakes here rather than paying a round trip to be
  // told the same thing in GoTrue's words. Email first, then password, then
  // name — the reading order of the form.
  const invalid = validateEmail(email) ?? validatePassword(password) ?? validateUsername(username);
  if (invalid) return { ok: false, error: invalid };
  // A trailing space typed into the email field is not a different account.
  const address = email.trim();
  const { data, error } = await client.auth.signUp({
    email: address,
    password,
    // emailRedirectTo sends the confirmation link back to the calling origin
    // (dev :5173 and the auth-e2e build :4174 can't share one site URL).
    options: {
      data: { username, ...(save ? { save } : {}) },
      ...(redirectTo ? { emailRedirectTo: redirectTo } : {}),
    },
  });
  if (error) {
    // The profile trigger's unique-index failure reaches us as a generic
    // database error; re-check availability to tell "taken" from real trouble.
    if (
      /database error/i.test(error.message) &&
      (await checkUsername(client, username)) === 'taken'
    ) {
      return { ok: false, error: 'That username is taken' };
    }
    return { ok: false, error: friendlySignupError(error.message) };
  }
  // GoTrue answers a signup for an existing, CONFIRMED email with a fake user
  // (enumeration protection): no session and an empty identities list — and
  // it sends nothing. Read as "check your email" that strands the player
  // (prod 2026-09-07: two new players re-registered up to 7 times waiting for
  // mail). An unconfirmed repeat keeps its identities and really is re-sent.
  if (data.user && data.user.identities?.length === 0) {
    return { ok: false, error: ALREADY_REGISTERED };
  }
  return { ok: true, needsConfirm: data.session === null };
}

export async function signIn(
  client: SupabaseClient,
  email: string,
  password: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { error } = await client.auth.signInWithPassword({ email, password });
  return error ? { ok: false, error: error.message } : { ok: true };
}

export async function signOutUser(client: SupabaseClient): Promise<void> {
  await client.auth.signOut();
}

/** Re-send the signup confirmation email (invalidates older links). */
export async function resendConfirmation(
  client: SupabaseClient,
  email: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { error } = await client.auth.resend({ type: 'signup', email });
  return error ? { ok: false, error: error.message } : { ok: true };
}

export async function loadProfile(client: SupabaseClient): Promise<{
  username: string;
  email: string;
  role: Role;
  userId: string;
  banned: boolean;
} | null> {
  const { data } = await client.auth.getUser();
  const user = data.user;
  if (!user) return null;
  const { data: row, error } = await client
    .from('profiles')
    .select('username, role, banned')
    .eq('id', user.id)
    .single();
  if (error || !row) return null;
  return {
    username: row.username as string,
    email: user.email ?? '',
    role: parseRole(row.role),
    userId: user.id,
    banned: row.banned === true,
  };
}

/**
 * Rename an account whose sign-in identity is derived from the player name.
 * Both the profile and auth.users.email must move, or the player is locked out.
 */
async function renameWithIdentity(
  client: SupabaseClient,
  name: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const base = import.meta.env.VITE_SUPABASE_URL;
  if (!base) return { ok: false, error: 'Accounts are not available in this build' };
  const { data: sessionData } = await client.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) return { ok: false, error: 'Not signed in' };

  try {
    const res = await fetch(`${String(base).replace(/\/$/, '')}/functions/v1/rename-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''),
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ username: name }),
    });
    const body = (await res.json()) as { ok?: boolean; error?: string };
    if (!res.ok || !body.ok) {
      return { ok: false, error: body.error ?? 'Could not change that name' };
    }
    // The sign-in address changed underneath this session; refresh so the
    // cached user carries the new one.
    await client.auth.refreshSession();
    return { ok: true };
  } catch {
    return { ok: false, error: 'Could not reach the server. Check your connection.' };
  }
}

export async function updateUsername(
  client: SupabaseClient,
  name: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const invalid = validateUsername(name);
  if (invalid) return { ok: false, error: invalid };
  const { data } = await client.auth.getUser();
  if (!data.user) return { ok: false, error: 'Not signed in' };

  // An account with no email signs in against an address DERIVED from its
  // player name, so renaming the profile alone would leave it signing in under
  // a name the player no longer knows — a permanent lockout for an under-14
  // with no email to recover through. The Edge Function moves both together.
  if (isSyntheticAddress(data.user.email ?? '')) {
    return renameWithIdentity(client, name);
  }

  const { error } = await client.from('profiles').update({ username: name }).eq('id', data.user.id);
  if (error) {
    return /duplicate|unique/i.test(error.message)
      ? { ok: false, error: 'That username is taken' }
      : { ok: false, error: error.message };
  }
  return { ok: true };
}

export async function updatePassword(
  client: SupabaseClient,
  password: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  // Same rules, same wording as signup — this field hits the same GoTrue check.
  const invalid = validatePassword(password);
  if (invalid) return { ok: false, error: invalid };
  const { error } = await client.auth.updateUser({ password });
  return error ? { ok: false, error: friendlySignupError(error.message) } : { ok: true };
}

// ── Admin tools ──────────────────────────────────────────────────────────────
// Thin wrappers over the SECURITY DEFINER RPCs; every permission decision
// (caller must be admin, rule 5, no setting 'admin') lives in the database.

export type AdminUserSummary = {
  id: string;
  username: string;
  role: Role;
  createdAt: string;
};

export type AdminUserDetail = AdminUserSummary & {
  coins: number | null;
  unlocked: number | null;
  bags: number | null;
  saveUpdatedAt: string | null;
};

export async function adminSearchUsers(
  client: SupabaseClient,
  query: string,
): Promise<{ ok: true; users: AdminUserSummary[] } | { ok: false; error: string }> {
  const { data, error } = await client.rpc('admin_search_users', { query });
  if (error) return { ok: false, error: error.message };
  const rows = (data ?? []) as Record<string, unknown>[];
  return {
    ok: true,
    users: rows.map((r) => ({
      id: r.id as string,
      username: r.username as string,
      role: parseRole(r.role),
      createdAt: r.created_at as string,
    })),
  };
}

export async function adminGetUser(
  client: SupabaseClient,
  id: string,
): Promise<{ ok: true; user: AdminUserDetail } | { ok: false; error: string }> {
  const { data, error } = await client.rpc('admin_get_user', { target: id });
  if (error) return { ok: false, error: error.message };
  const row = ((data ?? []) as Record<string, unknown>[])[0];
  if (!row) return { ok: false, error: 'User not found' };
  return {
    ok: true,
    user: {
      id: row.id as string,
      username: row.username as string,
      role: parseRole(row.role),
      createdAt: row.created_at as string,
      coins: (row.coins as number | null) ?? null,
      unlocked: (row.unlocked as number | null) ?? null,
      bags: (row.bags as number | null) ?? null,
      saveUpdatedAt: (row.save_updated_at as string | null) ?? null,
    },
  };
}

export async function adminSetRole(
  client: SupabaseClient,
  id: string,
  role: Role,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { data, error } = await client.rpc('admin_set_role', {
    target: id,
    new_role: role,
  });
  if (error) return { ok: false, error: error.message };
  // false = target was an admin, was the caller, or doesn't exist — the
  // server never distinguishes and neither do we.
  if (data !== true) return { ok: false, error: 'Not allowed for this user.' };
  return { ok: true };
}

// ── Dev commands ─────────────────────────────────────────────────────────────
// Thin wrappers over dev_give/dev_message; the ladder and all validation live
// in the database. The jsonb result comes back as a plain object.

export type Delivered = { delivered: 'global' } | { delivered: 'queued'; to: string };

export async function devGive(
  client: SupabaseClient,
  target: string,
  what: string,
  value: string,
): Promise<({ ok: true } & Delivered) | { ok: false; error: string }> {
  const { data, error } = await client.rpc('dev_give', { target, what, value });
  if (error) return { ok: false, error: error.message };
  return { ok: true, ...(data as Delivered) };
}

export async function devMessage(
  client: SupabaseClient,
  target: string,
  text: string,
): Promise<({ ok: true } & Delivered) | { ok: false; error: string }> {
  const { data, error } = await client.rpc('dev_message', { target, message: text });
  if (error) return { ok: false, error: error.message };
  return { ok: true, ...(data as Delivered) };
}

export async function devBan(
  client: SupabaseClient,
  target: string,
): Promise<{ ok: true; banned: string } | { ok: false; error: string }> {
  const { data, error } = await client.rpc('dev_ban', { target });
  if (error) return { ok: false, error: error.message };
  return { ok: true, banned: (data as { banned: string }).banned };
}

export async function devOp(
  client: SupabaseClient,
  target: string,
): Promise<({ ok: true } & Delivered) | { ok: false; error: string }> {
  const { data, error } = await client.rpc('dev_op', { target });
  if (error) return { ok: false, error: error.message };
  return { ok: true, ...(data as Delivered) };
}

/** `/skip <I|user>`: queue a skip for one player (higher_dev and admin). */
export async function devSkip(
  client: SupabaseClient,
  target: string,
): Promise<({ ok: true } & Delivered) | { ok: false; error: string }> {
  const { data, error } = await client.rpc('dev_skip', { target });
  if (error) return { ok: false, error: error.message };
  return { ok: true, ...(data as Delivered) };
}

/**
 * `/remove <I|user> …` (spec 2026-10-06, admin only): queue /give in reverse
 * for one player. `value` is the tower/item/potion name ('' otherwise).
 */
export async function devRemove(
  client: SupabaseClient,
  target: string,
  what: string,
  value: string,
  amount: number,
): Promise<({ ok: true } & Delivered) | { ok: false; error: string }> {
  const { data, error } = await client.rpc('dev_remove', { target, what, value, amount });
  if (error) return { ok: false, error: error.message };
  return { ok: true, ...(data as Delivered) };
}

/** `/restock potionshop`: move the Potion Shop epoch to now, for everyone. */
export async function devRestock(
  client: SupabaseClient,
): Promise<{ ok: true; delivered: 'global' } | { ok: false; error: string }> {
  const { error } = await client.rpc('dev_restock_potionshop');
  if (error) return { ok: false, error: error.message };
  return { ok: true, delivered: 'global' };
}

/** Signup pre-step: free the email if (and only if) a banned account holds it. */
